// ============================================
// Lazy Image Component (ES6)
// Skeleton UI + 비동기 로드 + ResizeObserver
// ============================================

/**
 * Skeleton 이미지 래퍼 생성
 * @param {string} src - 이미지 URL
 * @param {string} alt - alt 텍스트
 * @param {string} skeletonClass - skeleton 크기 클래스 (chat-image-skeleton, scene-image-skeleton 등)
 * @param {Object} options - 추가 옵션
 * @param {Function} options.onClick - 클릭 핸들러
 * @param {Function} options.onLoad - 로드 완료 콜백
 * @param {Function} options.onError - 에러 콜백
 * @returns {HTMLElement} 래퍼 엘리먼트
 */
export function createLazyImage(src, alt, skeletonClass = 'chat-image-skeleton', options = {}) {
  const wrapper = document.createElement('div');
  wrapper.className = 'lazy-image-wrapper';

  // Skeleton placeholder
  const skeleton = document.createElement('div');
  skeleton.className = `skeleton ${skeletonClass}`;
  wrapper.appendChild(skeleton);

  // 실제 이미지 (숨김 상태로 시작)
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt || '';
  img.loading = 'lazy';
  img.style.cssText = 'opacity: 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; transition: opacity 0.3s ease;';

  if (options.onClick) {
    img.style.cursor = 'pointer';
    wrapper.style.cursor = 'pointer';
    wrapper.addEventListener('click', options.onClick);
  }

  img.onload = () => {
    skeleton.remove();
    img.style.position = 'relative';
    img.style.opacity = '1';
    wrapper.classList.add('loaded');

    // 콜백 실행 (스크롤 조정용)
    if (options.onLoad) {
      options.onLoad(img);
    }
  };

  img.onerror = () => {
    skeleton.remove();
    // 에러 메시지 표시 (DOM API 사용)
    const errorDiv = document.createElement('div');
    errorDiv.className = 'image-error';
    errorDiv.textContent = '이미지 로드 실패';
    wrapper.appendChild(errorDiv);

    if (options.onError) {
      options.onError();
    }
  };

  wrapper.appendChild(img);

  return wrapper;
}

/**
 * 기존 img 태그를 lazy 로딩으로 업그레이드
 * (이미 존재하는 img-loading-wrapper 패턴과 호환)
 * @param {HTMLElement} imgElement - img 엘리먼트
 * @param {string} skeletonClass - skeleton 크기 클래스
 */
export function upgradToLazyImage(imgElement, skeletonClass = 'chat-image-skeleton') {
  if (!imgElement || imgElement.tagName !== 'IMG') return;

  const parent = imgElement.parentElement;
  if (!parent) return;

  // 이미 로드된 이미지는 스킵
  if (imgElement.complete && imgElement.naturalHeight !== 0) {
    imgElement.classList.add('loaded');
    return;
  }

  // Skeleton 추가
  const skeleton = document.createElement('div');
  skeleton.className = `skeleton ${skeletonClass}`;
  parent.insertBefore(skeleton, imgElement);

  // 이미지 스타일
  imgElement.style.opacity = '0';
  imgElement.style.transition = 'opacity 0.3s ease';

  imgElement.onload = () => {
    skeleton.remove();
    imgElement.style.opacity = '1';
    imgElement.classList.add('loaded');
  };

  imgElement.onerror = () => {
    skeleton.remove();
  };
}

/**
 * ResizeObserver 기반 스크롤 자동 조정
 * 이미지 로드 후 컨테이너 크기가 변하면 자동으로 스크롤 최하단 유지
 * @param {HTMLElement} container - 메시지 컨테이너
 * @returns {ResizeObserver|null} observer 인스턴스 (정리용)
 */
export function setupScrollObserver(container) {
  if (!container) return null;

  let isAtBottom = true;
  let lastScrollTop = 0;
  let lastScrollHeight = 0;

  // 스크롤 위치 추적 (사용자가 위로 스크롤했는지 감지)
  const scrollHandler = () => {
    const { scrollTop, scrollHeight, clientHeight } = container;
    // 하단 50px 이내면 "하단에 있다"고 판단
    isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
    lastScrollTop = scrollTop;
  };

  container.addEventListener('scroll', scrollHandler, { passive: true });

  // 콘텐츠 크기 변화 감지 (이미지 로드 시 높이 변경)
  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const newScrollHeight = container.scrollHeight;

      // 스크롤 높이가 증가했고, 사용자가 하단에 있었다면 스크롤 유지
      if (newScrollHeight > lastScrollHeight && isAtBottom) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }

      lastScrollHeight = newScrollHeight;
    }
  });

  // 메시지 컨테이너 자체의 크기 변화 관찰
  resizeObserver.observe(container);

  // 정리 함수 반환
  resizeObserver._cleanup = () => {
    container.removeEventListener('scroll', scrollHandler);
    resizeObserver.disconnect();
  };

  return resizeObserver;
}

/**
 * 스크롤 observer 정리
 * @param {ResizeObserver} observer - setupScrollObserver에서 반환된 observer
 */
export function cleanupScrollObserver(observer) {
  if (observer && observer._cleanup) {
    observer._cleanup();
  } else if (observer) {
    observer.disconnect();
  }
}
